import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Grid, CircularProgress, Alert, Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../services/api';
import { format } from 'date-fns';

// Use a default color scheme if styleGuide is not available
const colors = {
  tableHeaderBackground: '#f5f5f5',
  lightBackground: '#f9f9f9',
  success: '#4caf50',
  error: '#f44336',
  info: '#2196f3',
  warning: '#ff9800'
};

const API_URL = API_BASE_URL;

const BalanceSheet = () => {
  const [asOfDate, setAsOfDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  
  const generateReport = async () => {
    if (!asOfDate) {
      setError('Please select a date');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        asOfDate: format(asOfDate, 'yyyy-MM-dd')
      };
      
      const response = await axios.get(`${API_URL}/accounting/balance-sheet`, {
        params,
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (err) {
      console.error('Error generating balance sheet:', err);
      setError('Failed to generate Balance Sheet. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'LKR' 
    }).format(amount);
  };
  
  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Balance Sheet
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="As of Date"
                value={asOfDate}
                onChange={(newValue) => setAsOfDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={4} md={6}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={generateReport}
              disabled={loading || !asOfDate}
              fullWidth
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Paper sx={{ p: 3 }} elevation={3}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Balance Sheet
            </Typography>
            <Typography variant="subtitle1">
              As of {format(new Date(report.asOfDate), 'MMMM d, yyyy')}
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {/* Assets Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                Assets
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.assets.accounts.length > 0 ? (
                      report.assets.accounts.map((account) => (
                        <TableRow key={account.id} hover>
                          <TableCell>{account.account_code} - {account.name}</TableCell>
                          <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          No asset accounts found
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{ backgroundColor: colors.lightBackground }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Assets</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(report.assets.total)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            
            {/* Liabilities and Equity Section */}
            <Grid item xs={12} md={6}>
              {/* Liabilities Section */}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                Liabilities
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.liabilities.accounts.length > 0 ? (
                      report.liabilities.accounts.map((account) => (
                        <TableRow key={account.id} hover>
                          <TableCell>{account.account_code} - {account.name}</TableCell>
                          <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          No liability accounts found
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{ backgroundColor: colors.lightBackground }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Liabilities</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(report.liabilities.total)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Equity Section */}
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                Equity
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.equity.accounts.length > 0 ? (
                      report.equity.accounts.map((account) => (
                        <TableRow key={account.id} hover>
                          <TableCell>{account.account_code} - {account.name}</TableCell>
                          <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          No equity accounts found
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow hover>
                      <TableCell>Retained Earnings</TableCell>
                      <TableCell align="right">{formatCurrency(report.equity.retainedEarnings)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: colors.lightBackground }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Equity</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(report.equity.total)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Total Liabilities and Equity */}
              <Box sx={{ p: 2, backgroundColor: colors.lightBackground, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Liabilities and Equity</span>
                  <span>{formatCurrency(report.totalLiabilitiesAndEquity)}</span>
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          {/* Accounting Equation Check */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            p: 2, 
            backgroundColor: Math.abs(report.assets.total - report.totalLiabilitiesAndEquity) < 0.01 ? colors.success : colors.error, 
            borderRadius: 1,
            color: 'white'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Accounting Equation: Assets = Liabilities + Equity
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(report.assets.total)} = {formatCurrency(report.totalLiabilitiesAndEquity)}
            </Typography>
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
};

export default BalanceSheet;
